import { Heading, PathShape, TileKind } from "../data/balance";
import { Cell, headingBetween, headingOffset, rotateCcw, rotateCw } from "./cell";

/** A concrete placement of a path shape in world cells, in walking order. */
export class SegmentPlacement {
  constructor(
    readonly shapeIndex: number,
    readonly cells: readonly Cell[],
    readonly tileKind: TileKind,
    readonly exitHeading: Heading,
    /** Index within the deterministic ghost list this placement was generated in. */
    readonly orientationIndex: number,
    /** Road pace it counts for: the shape's `paceCost` (DESIGN §4.4, §4.7), its cell count unless a special tile says otherwise. */
    readonly paceCost: number = cells.length,
  ) {}

  sameCells(o: SegmentPlacement): boolean {
    if (this.cells.length !== o.cells.length) return false;
    for (let i = 0; i < this.cells.length; i++) if (!this.cells[i].equals(o.cells[i])) return false;
    return true;
  }
}

/**
 * Enumerates every distinct placement of a shape at an open end in a fixed order:
 * forward (straight, left, right) × mirror (false, true), deduplicated by cell list.
 * At the spawn only the spawn heading is allowed as forward.
 */
export function enumeratePlacements(shape: PathShape, openEnd: Cell, heading: Heading, atSpawn: boolean): SegmentPlacement[] {
  const forwards = atSpawn ? [heading] : [heading, rotateCcw(heading), rotateCw(heading)];
  const scratch: SegmentPlacement[] = [];
  for (const f of forwards) {
    for (let m = 0; m < 2; m++) {
      const p = build(shape, openEnd, f, m === 1);
      if (!scratch.some((e) => e.sameCells(p))) scratch.push(p);
    }
  }
  return scratch.map((p, i) => new SegmentPlacement(p.shapeIndex, p.cells, p.tileKind, p.exitHeading, i, p.paceCost));
}

function build(shape: PathShape, origin: Cell, forward: Heading, mirror: boolean): SegmentPlacement {
  const [fx, fy] = headingOffset(forward);
  let [rx, ry] = headingOffset(rotateCw(forward));
  if (mirror) { rx = -rx; ry = -ry; }
  const cells = shape.tiles.map((t) => new Cell(origin.x + t.x * rx + t.y * fx, origin.y + t.x * ry + t.y * fy));
  const n = cells.length;
  const exit = n >= 2 ? headingBetween(cells[n - 2], cells[n - 1]) : forward;
  return new SegmentPlacement(shape.index, cells, shape.tileKind, exit, 0, shape.paceCost);
}
